import React, { useContext, useState } from "react";
import { KeyboardNavigationContext } from "../components/keyboardNavigation";
import {
  Coord,
  KeyboardNavigationContextValue,
  NavigationDirection,
} from "../types/keyboardNavigation";
import { useDispatch, useSelector } from "react-redux";
import { ActionType, AppDispatch, RootState } from "../reducer";
import { SetShiftDownAction } from "../reducer/keyboardNavigation";

export const useKeyboardNavigation: () => KeyboardNavigationContextValue =
  () => {
    return useContext(KeyboardNavigationContext);
  };

export const useNavigateCoords: () => (
  coord: Coord,
  direction: NavigationDirection
) => Coord = () => {
  const { navigationMap, columnMajor } = useKeyboardNavigation();

  return (coord, direction) => {
    const { col, row } = coord;
    const rowOffset =
      direction === NavigationDirection.UP
        ? -1
        : direction === NavigationDirection.DOWN
        ? 1
        : 0;
    const colOffset =
      direction === NavigationDirection.RIGHT
        ? 1
        : direction === NavigationDirection.LEFT
        ? -1
        : 0;
    const newRowCandidate = row + rowOffset;
    const newColCandidate = col + colOffset;
    const [newCol, newRow] = navigationMap[newColCandidate]?.[newRowCandidate]
      ? [newColCandidate, newRowCandidate]
      : [col, row];
    // console.log(
    //   "NAV_COORDS",
    //   JSON.stringify(
    //     {
    //       row,
    //       col,
    //       newRow,
    //       newCol,
    //     },
    //     null,
    //     4
    //   )
    // );

    return { col: newCol, row: newRow };
  };
};

export const useNavigate: () => (
  coord: Coord,
  direction: NavigationDirection
) => Coord = () => {
  const navigateCoords = useNavigateCoords();
  const coordToId = useCoordToId();

  return (coord, direction) => {
    const targetCoord = navigateCoords(coord, direction);
    const targetId = coordToId(targetCoord);
    const targetElement = document.getElementById(targetId);
    targetElement?.focus();
    return targetCoord;
  };
};

export const useIsShiftDown: () => [
  boolean,
  (isShiftDown: boolean) => void
] = () => {
  const isShiftDown = useSelector((state: RootState) => state.ui.isShiftDown);
  const dispatch = useDispatch<AppDispatch>();
  const setShiftDown = (isShiftDown: boolean) => {
    const action: SetShiftDownAction = {
      type: ActionType.SET_SHIFT_DOWN,
      isShiftDown,
    };
    dispatch(action as any);
  };
  return [isShiftDown, setShiftDown];
};

export const useEventHandlers: (id: string) => {
  onKeyDown: React.KeyboardEventHandler;
  onKeyUp: React.KeyboardEventHandler;
} = (id) => {
  const idToCoord = useIdToCoord();
  const navigate = useNavigate();
  const coord = idToCoord(id);
  const [isShiftDown, setIsShiftDown] = useIsShiftDown();

  const onKeyDown: React.KeyboardEventHandler = (e) => {
    const key = e.key;
    if (key === "Shift") {
      setIsShiftDown(true);
    }
    if (key === "Tab") {
      e.preventDefault();
      e.stopPropagation();
      isShiftDown
        ? navigate(coord, NavigationDirection.LEFT)
        : navigate(coord, NavigationDirection.RIGHT);
    }
    if (key === "ArrowRight") {
      e.preventDefault();
      e.stopPropagation();
      navigate(coord, NavigationDirection.RIGHT);
    }
    if (key === "ArrowLeft") {
      e.preventDefault();
      e.stopPropagation();
      navigate(coord, NavigationDirection.LEFT);
    }
    if (key === "ArrowDown") {
      e.preventDefault();
      e.stopPropagation();
      navigate(coord, NavigationDirection.DOWN);
    }
    if (key === "ArrowUp") {
      e.preventDefault();
      e.stopPropagation();
      navigate(coord, NavigationDirection.UP);
    }
  };

  const onKeyUp: React.KeyboardEventHandler = (e) => {
    const key = e.key;
    if (key === "Shift") {
      setIsShiftDown(false);
    }
  };

  return { onKeyDown, onKeyUp };
};

export const useCoordToId: () => (coord: Coord) => string = () => {
  const { navigationMap, columnMajor } = useKeyboardNavigation();

  return ({ row, col }) => {
    return columnMajor ? navigationMap[col][row] : navigationMap[row][col];
  };
};

export const useIdToCoord: () => (id: string) => Coord = () => {
  const { navigationMap, columnMajor } = useKeyboardNavigation();
  const idToCoordMap: { [id: string]: Coord } = Object.fromEntries(
    navigationMap.flatMap((group, xIndex) =>
      group.map((y, element) => {
        const coord: Coord = columnMajor
          ? { col: xIndex, row: element }
          : { col: element, row: xIndex };
        return [y, coord];
      })
    )
  );

  return (id) => {
    return idToCoordMap[id];
  };
};
